/**
 * LanguageToggle - compact UI (app) language switch surfaced in the main
 * navigation so the language choice is discoverable without opening Settings
 * (pirx-syfd, 2026-07-03 feedback "make language change more intuitive").
 *
 * Binds to the SAME app-language preference as Settings via applyAppLanguageChange
 * (localStorage + i18next + best-effort backend sync). This switches only the UI
 * language; the analysis/output language is an independent control on Upload +
 * Settings (pirx-m6ks). Adds no new capability - it only exposes the existing
 * control at a discoverable spot.
 *
 * pirx-f8ioo (2026-07-18, cosmetic discoverability): added a Globe icon as
 * visual affordance + a hover tooltip on the wrapper so the control reads as
 * a language switcher at a glance. No behavioural change, no data-test churn.
 */

import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { updateUserPreferences } from "@/services/api-client";
import {
  type AppLanguage,
  applyAppLanguageChange,
} from "@/utils/language-preference";

const LANGUAGES: readonly AppLanguage[] = ["pl", "en"];

export function LanguageToggle() {
  const { t, i18n } = useTranslation("common");
  // Single source of truth for the UI language - i18next's resolved
  // language, shared with the Settings preferences handler. Avoids two
  // independent local-state snapshots drifting out of sync (vardic review
  // 2026-08-02).
  const language: AppLanguage = i18n.resolvedLanguage === "en" ? "en" : "pl";

  const handleSelect = (next: AppLanguage) => {
    if (next === language) return;
    const preferences = applyAppLanguageChange(next);
    // Best-effort durable sync so the choice follows the user across browsers;
    // localStorage + i18next are already updated by applyAppLanguageChange.
    void updateUserPreferences(preferences).catch(() => {});
  };

  return (
    <div
      role="group"
      aria-label={t("language.switchLabel")}
      title={t("language.switchLabel")}
      data-test="app-language-toggle"
      className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] p-0.5 pl-1.5"
    >
      <Globe
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 text-[var(--muted-foreground)]"
      />
      {LANGUAGES.map((code) => {
        const isActive = code === language;
        return (
          <Button
            key={code}
            variant="ghost"
            size="xs"
            data-test={`app-language-${code}`}
            aria-pressed={isActive}
            aria-label={t(`language.names.${code}`)}
            onClick={() => handleSelect(code)}
            className={cn(
              "rounded-full",
              isActive
                ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
                : "text-[var(--muted-foreground)]",
            )}
          >
            {t(`language.code.${code}`)}
          </Button>
        );
      })}
    </div>
  );
}